/**
 * The session view (D156) — what a shell renders, and nothing else.
 *
 * @remarks
 * Every derived view the shell used to compute for itself from
 * `(bundle, state)` — the map, the journal, the voice index, the room
 * scene, the ambience, the countdown, the question's chips — is ONE shape
 * here, produced by the host package's projection. In client mode the
 * projection runs in the browser; in server mode it runs on `play-api` and
 * arrives as JSON. The shell cannot tell the difference, which is the point:
 * a server game's client renders a view and never holds a world.
 *
 * What is deliberately ABSENT: any flag, any exit's lock state, any item's
 * location, the pack's text the player has not yet earned. The engine's
 * `RuntimeState` never crosses this boundary; only its projection does.
 *
 * Asset paths (`src`, `url`) arrive already resolved against the game's
 * asset base — the shell puts them straight into `<img>` and the audio
 * player.
 *
 * @packageDocumentation
 */

import {
	audioCueLayerSchema,
	mapRoomKindSchema,
	mapStyleSchema,
	recordLayerSchema,
	roomIdSchema,
	runtimeTranscriptEntrySchema,
	transcriptPrioritySchema,
	transcriptSourceSchema,
} from '@orpheus/content-schema';
import { z } from 'zod';

/** A string a shell may show. Never blank — a blank label is a bug upstream, not a rendering choice. */
const label = z.string().min(1);

/** A resolved URL or path a shell may fetch as is. */
const assetUrl = z.string().min(1);

/** A bed to play by URL, keyed so a repeated target is a no-op, or deliberate quiet. */
export const ambienceTargetSchema = z.union([
	z.literal('silence'),
	z.strictObject({ key: label, url: assetUrl }).readonly(),
]);

export type AmbienceTarget = z.infer<typeof ambienceTargetSchema>;

/**
 * D264: one sound a turn made — its key (the audio id), its file by URL, and whether it is music the bed goes down
 * under. D265: and the transcript entry whose line says it, so the shell sounds it as the reveal begins that line;
 * absent for a line that is no entry (the wall's), which sounds as the turn arrives.
 */
export const cueViewSchema = z.strictObject({ entryId: label.optional(), key: label, layer: audioCueLayerSchema, url: assetUrl }).readonly();

export type CueView = z.infer<typeof cueViewSchema>;

/** One chip under a question: what it says, what it submits, and its stable key. */
export const commandSuggestionSchema = z
	.strictObject({
		label,
		/** Submitted as the player's own words — a bare name, never a structured command (D37). */
		submitValue: label,
		suggestionKey: label,
		/**
		 * D179: `danger` marks the chip that does something irreversible — a
		 * confirm's START OVER — so the shell draws it in the danger color, the
		 * promise the title screen's BEGIN AGAIN makes with the same color.
		 */
		tone: z.enum(['danger']).optional(),
	})
	.readonly();

export type CommandSuggestion = z.infer<typeof commandSuggestionSchema>;

/** The parked question's face and its chips. Completion questions get no chips, by ruling; a confirm's chips ARE its buttons (D179); a dialogue's chips are the answers the actor offered (D235). */
export const pendingQuestionViewSchema = z
	.strictObject({
		kind: z.enum(['completion', 'confirm', 'dialogue', 'which_entity']),
		questionText: label,
		suggestions: z.array(commandSuggestionSchema).readonly(),
	})
	.readonly();

export type PendingQuestionView = z.infer<typeof pendingQuestionViewSchema>;

/** One fuzzy-matched token the engine corrected, so the shell can show "reading X as Y". */
export const tokenCorrectionViewSchema = z
	.strictObject({
		correctedText: label,
		originalText: label,
	})
	.readonly();

export type TokenCorrectionView = z.infer<typeof tokenCorrectionViewSchema>;

const mapKnowledge = z.enum(['revealed', 'unknown', 'visited']);

/** One placed room (or the unknown stub past a known exit) on the grid. */
const mapGraphNodeSchema = z
	.strictObject({
		isActive: z.boolean(),
		isCheckpoint: z.boolean(),
		kind: mapRoomKindSchema,
		knowledge: mapKnowledge,
		level: z.int(),
		/** Absent on an `unknown` stub — the map shows what the player knows, never the authored world. */
		name: z.string().optional(),
		roomId: roomIdSchema.optional(),
		x: z.int(),
		y: z.int(),
	})
	.readonly();

/**
 * The map as the engine derives it (D34 the grid, D169 the floors) — the
 * THING DRAWN rather than any drawing of it. D209 put this on the wire in
 * place of a laid-out picture, so the shell lays it out in whichever of the
 * pack's offered styles the player chose, with the same functions the
 * engine's tests pin. The shape is `engine-runtime`'s `MapGraph` field for
 * field; the host's projection assigns one to the other, so the two cannot
 * drift without a compile error.
 */
export const mapGraphSchema = z
	.strictObject({
		/** Links the grid cannot draw honestly: non-cardinal, outward, non-adjacent — and, on a flat graph, every `up` and `down`. */
		annotations: z.array(z.strictObject({ directionToken: label, fromRoomName: label, toLabel: label }).readonly()).readonly(),
		bounds: z.strictObject({ maxLevel: z.int(), maxX: z.int(), maxY: z.int(), minLevel: z.int(), minX: z.int(), minY: z.int() }).readonly(),
		edges: z
			.array(z.strictObject({ directionToken: label, fromX: z.int(), fromY: z.int(), level: z.int(), toX: z.int(), toY: z.int() }).readonly())
			.readonly(),
		/** D169: the vertical links, on a levelled graph; empty on a flat one. */
		lifts: z.array(z.strictObject({ directionToken: label, fromLevel: z.int(), toLevel: z.int(), x: z.int(), y: z.int() }).readonly()).readonly(),
		nodes: z.array(mapGraphNodeSchema).readonly(),
		visitedCount: z.int().nonnegative(),
	})
	.readonly();

export type MapGraph = z.infer<typeof mapGraphSchema>;

/**
 * The map (D209): the graph in each flavor the pack's offered styles need,
 * the styles the player may choose among, the one the session opens on,
 * and the pack's name and tint for it. The host describes; the shell
 * chooses and draws. A pack that offers one style sends one graph.
 */
export const mapViewSchema = z
	.strictObject({
		/** The graph per flavor — `flat` for the plan, `levelled` for the hologram — present for exactly the flavors `styles` need. */
		graphs: z.strictObject({ flat: mapGraphSchema.optional(), levelled: mapGraphSchema.optional() }).readonly(),
		/** The map the session opens on — always one of `styles`. */
		style: mapStyleSchema,
		/** The styles the player may choose among, in chooser order; one entry is a lock (no chooser). */
		styles: z.array(mapStyleSchema).min(1).readonly(),
		/** The pack's tint for the hologram's material, when it chose one; absent, the brand's. */
		tint: z.string().regex(/^#[0-9a-fA-F]{6}$/u).optional(),
		/** What the pack calls its map — the panel heading and the figure's accessible name. */
		title: label,
		/** How many rooms the player has walked — the trigger's count, the same in every flavor. */
		visitedCount: z.int().nonnegative(),
	})
	.readonly()
	// The plan reads the flat graph and every other style the levelled one;
	// a view that offers a style must carry the graph it draws from, or the
	// shell's chooser would offer a map that cannot be drawn.
	.refine((map) => map.styles.every((style) => map.graphs[style === 'basic' ? 'flat' : 'levelled'] !== undefined), {
		message: 'map.graphs must carry the flavor every offered style draws from: flat for basic, levelled for the rest',
		path: ['graphs'],
	});

export type MapView = z.infer<typeof mapViewSchema>;

/** One authored line, resolved against the world: plain text on a channel at a priority. */
export const resolvedLineSchema = z
	.strictObject({
		priority: transcriptPrioritySchema,
		source: transcriptSourceSchema,
		text: label,
	})
	.readonly();

export type ResolvedLine = z.infer<typeof resolvedLineSchema>;

/** One journal entry: a read document, re-read live. */
export const journalEntryViewSchema = z
	.strictObject({
		foundInRoomName: label.optional(),
		lines: z.array(resolvedLineSchema).readonly(),
		logId: label,
		title: label,
	})
	.readonly();

export type JournalEntryView = z.infer<typeof journalEntryViewSchema>;

/** What a voice was doing when it spoke (R22). */
export const voiceIndexActSchema = z.discriminatedUnion('kind', [
	z.strictObject({ kind: z.literal('arrival'), roomName: label }).readonly(),
	z.strictObject({ kind: z.literal('authored_beat') }).readonly(),
	z.strictObject({ kind: z.literal('ending') }).readonly(),
	z.strictObject({ kind: z.literal('engine_response'), response: z.enum(['empty_directive', 'refusal', 'teach']) }).readonly(),
	z.strictObject({ kind: z.literal('unattributed') }).readonly(),
	z
		.strictObject({ kind: z.literal('verb'), objectName: label.optional(), subjectName: label.optional(), verb: label })
		.readonly(),
]);

/** One line of the voice index: what she said, by the act. */
export const voiceIndexEntryViewSchema = z
	.strictObject({
		act: voiceIndexActSchema,
		actLabel: label,
		duringInput: z.string().optional(),
		text: label,
		turnCount: z.int().nonnegative(),
	})
	.readonly();

export type VoiceIndexEntryView = z.infer<typeof voiceIndexEntryViewSchema>;

/**
 * One declared speaker, as the shell needs it (D192): the id the transcript's
 * `source` carries, the name the player sees, how the lines arrive and in
 * what color. A pack may declare none — then there are no voices, no Voices
 * dialog, and every line is one of the engine's four fixed sources.
 */
export const speakerViewSchema = z
	.strictObject({
		color: z.string().regex(/^#[0-9a-fA-F]{6}$/u).optional(),
		/** D232: the name an EMBODIED speaker's lines carry as real text — present only for an actor's voice. */
		lineLabel: label.optional(),
		name: label,
		reveal: z.strictObject({ cadence: z.enum(['focus', 'instant', 'stepped']), stepMs: z.int().positive().optional() }).readonly(),
		speakerId: label,
	})
	.readonly();

export type SpeakerView = z.infer<typeof speakerViewSchema>;

/** One speaker's index (R22; D192 per speaker): what that voice has said, by the act — in the pack's declared order. */
export const voiceIndexViewSchema = z
	.strictObject({
		entries: z.array(voiceIndexEntryViewSchema).readonly(),
		name: label,
		speakerId: label,
	})
	.readonly();

export type VoiceIndexView = z.infer<typeof voiceIndexViewSchema>;

/**
 * A sprite composited onto the plate (D96/D146): source, placement, and how
 * it blends — and, when the pack provides one, the still a reduced-motion
 * viewer sees in its place (D182). The choice between the two is the
 * renderer's, because only the renderer knows the viewer. So is D255's
 * `transition`: whether the player just watched this sprite replace
 * `fromSpriteId` is a fact about the screen, not the world.
 */
export const sceneSpriteSchema = z
	.strictObject({
		compositing: z.strictObject({ blend: z.enum(['normal', 'screen']), opacity: z.number().min(0).max(1) }).readonly(),
		placement: z.strictObject({ width: z.number(), x: z.number(), y: z.number() }).readonly(),
		reducedMotionSrc: assetUrl.optional(),
		spriteId: label,
		src: assetUrl,
		transition: z.strictObject({ fromSpriteId: label, src: assetUrl }).readonly().optional(),
	})
	.readonly();

/** The room's picture: the plate and every sprite currently true, committed together — each with its still when it moves (D182, D263). */
export const roomSceneSchema = z
	.strictObject({
		plate: z.strictObject({ alt: z.string(), reducedMotionSrc: assetUrl.optional(), src: assetUrl }).readonly(),
		sprites: z.array(sceneSpriteSchema).readonly(),
	})
	.readonly();

export type RoomScene = z.infer<typeof roomSceneSchema>;

/** The clock, resolved for this turn (D153). */
export const pacingViewSchema = z
	.strictObject({
		autoWaitMs: z.int().positive(),
		countdown: z.strictObject({ label, turnsRemaining: z.int().nonnegative() }).readonly().optional(),
	})
	.readonly();

export type PacingView = z.infer<typeof pacingViewSchema>;

/** Everything a shell renders for one session at one moment. */
export const sessionViewSchema = z
	.strictObject({
		/** An opaque token for the room, so a shell can tell "same room" from "new room" for its transitions. */
		activeRoomKey: label,
		ambience: ambienceTargetSchema.optional(),
		/** The player's own words, oldest first — the input line's history. */
		commandHistory: z.array(z.string()).readonly(),
		/** Whether a terminal ending has been reached — the story is over, the input line closes. */
		isEnded: z.boolean(),
		journal: z.array(journalEntryViewSchema).readonly(),
		map: mapViewSchema,
		pacing: pacingViewSchema.optional(),
		pendingQuestion: pendingQuestionViewSchema.optional(),
		scene: roomSceneSchema.optional(),
		/** D192: the voices the pack declared, in declared order — empty when it declared none. */
		speakers: z.array(speakerViewSchema).readonly(),
		transcript: z.array(runtimeTranscriptEntrySchema).readonly(),
		turnCount: z.int().nonnegative(),
		/** One index per declared speaker, in the same order as `speakers`. */
		voices: z.array(voiceIndexViewSchema).readonly(),
	})
	.readonly();

export type SessionView = z.infer<typeof sessionViewSchema>;

/**
 * The engine's verdict on one input, named the way the engine names it —
 * plus the platform's one verdict (D165): `not_entitled`, the turn that
 * would have carried the player past the demo, refused before it was
 * appended, so the replay never saw it.
 */
export const turnVerdictSchema = z.enum([
	'action_rejected',
	'clarification_required',
	'executed',
	'not_entitled',
	'parse_failed',
	'reaction',
	'session_ended',
]);

export type TurnVerdict = z.infer<typeof turnVerdictSchema>;

/** What one turn hands back: the whole view after it, plus what the shell shows about the input itself. */
export const turnOutcomeSchema = z
	.strictObject({
		corrections: z.array(tokenCorrectionViewSchema).readonly(),
		/**
		 * D264: the sounds this turn's lines made, in the order they were said —
		 * present only when one did. A TURN's, never a view's: opening, loading
		 * or restoring a session answers with a view and no outcome, so nothing
		 * already on the screen is ever heard twice.
		 */
		cues: z.array(cueViewSchema).min(1).readonly().optional(),
		/** D165: with `not_entitled`, the game's own words for the edge of the demo — shown in the transcript's voice, never as a modal. */
		paywall: z.array(resolvedLineSchema).min(1).readonly().optional(),
		/**
		 * D241: the layers this turn wrote into the Record, handed back for the
		 * DEVICE to keep — present only when the server holds no account to keep
		 * them with (a player not signed in). A signed-in player's are kept with
		 * the account by the Worker, and never cross the wire.
		 */
		recordLayersForDevice: z.array(recordLayerSchema).min(1).readonly().optional(),
		verdict: turnVerdictSchema,
		view: sessionViewSchema,
	})
	.readonly();

export type TurnOutcome = z.infer<typeof turnOutcomeSchema>;
